import { type CSSProperties, type FC, type ReactNode, useEffect } from "react";
import Card from "~/common/components/Card";
import classNames from "classnames";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { Trans, useTranslation } from "react-i18next";
import { IconSize } from "~/common/components/icon/IconSize";
import { MarketingConsentCheckbox } from "~/features/registration/components/MediasRegistrationBanner/MarketingConsentCheckbox";
import { styled } from "~/common/mui";
import { C_SURFACE } from "~/common/components/icon/Colors";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";

import "./newsletter.less";

interface Props {
  className?: string;
  selected: boolean;
  setSelected: (val: boolean) => void;
}

export const MarketingConsent = ({ selected, setSelected, className }: Props) => {
  const { t } = useTranslation();

  return (
    <MarketingConsent.Card className={className}>
      <MarketingConsent.Title>{t("newsletter.consent.title")}</MarketingConsent.Title>
      <MarketingConsent.ConsentCheckbox selected={selected} setSelected={setSelected} />
      <MarketingConsent.Title className={"mt-30"}>{t("newsletter.reason.title")}</MarketingConsent.Title>
      <MarketingConsent.Reasons reasons={DEFAULT_REASONS} />
    </MarketingConsent.Card>
  );
};

MarketingConsent.Card = Card;

const DEFAULT_REASONS = [
  {
    icon: IconType.Cup,
    children: <Trans i18nKey={"newsletter.reason.1"} />,
  },
  {
    icon: IconType.ScreenNews,
    children: <Trans i18nKey={"newsletter.reason.2"} />,
  },
  {
    icon: IconType.PriceTag,
    children: <Trans i18nKey={"newsletter.reason.3"} />,
  },
  {
    icon: IconType.Invitation,
    children: <Trans i18nKey={"newsletter.reason.4"} />,
  },
  {
    icon: IconType.Lightbulb,
    children: <Trans i18nKey={"newsletter.reason.5"} />,
  },
];

interface ConsentCheckboxProps {
  className?: string;
  selected?: boolean;
  setSelected?: (val: boolean) => void;
  mandatory?: boolean;
  hideTitle?: boolean;
  disabled?: boolean;
}

MarketingConsent.ConsentCheckbox = ({
  selected,
  setSelected,
  mandatory,
  disabled,
  hideTitle,
  className,
}: ConsentCheckboxProps) => {
  const renderText = (suffix: string) => <Trans i18nKey={`newsletter.consent.first.${suffix}`} />;

  useEffect(() => {
    const nodes = Array.prototype.slice.call(document.querySelectorAll(".js__newsletter a:not([href])"));
    nodes.forEach((node: HTMLAnchorElement) => {
      node.href = `mailto:${node.text}`;
    });
  });

  return (
    <div className={classNames("scha__newsletter__consent-item js__newsletter", className)}>
      {!hideTitle && <p className={"scha__newsletter__consent-item__subtitle"}>{renderText("title")}</p>}
      <MarketingConsentCheckbox
        data-test-id={"newsletter-consent-checkbox"}
        checked={selected}
        onCheck={setSelected}
        centerCheckbox={false}
        disabled={disabled}
      />
    </div>
  );
};

MarketingConsent.Title = styled("p")({
  ...getFontStyle(FontSize.TitleLarge),
  marginBottom: "24px",
});

interface ReasonsProps {
  className?: string;
  reasons: ReasonProps[];
}

MarketingConsent.Reasons = styled(({ reasons, className }: ReasonsProps) => {
  return (
    <div className={className}>
      {reasons.map((reason, index) => (
        <Reason key={index} icon={reason.icon}>
          {reason.children}
        </Reason>
      ))}
    </div>
  );
})(({ theme }) => ({
  backgroundColor: C_SURFACE,
  display: "flex",
  flexFlow: "column",
  padding: "8px",
  gap: "32px",
  [theme.breakpoints.up("tablet")]: {
    padding: "32px",
    flexFlow: "row",
    "> *": {
      flex: "1",
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    paddingInline: "64px",
  },
}));

interface ReasonProps {
  icon: IconType;
  children?: ReactNode;
}
const Reason = ({ icon, children }: ReasonProps) => (
  <div className={"scha__newsletter__reason"}>
    <Circle size={"80px"} className={"bg-white mb-3"}>
      <Icon type={icon} size={IconSize.FONT_30} className={"f-green"} />
    </Circle>
    <div className={"f-center"}>{children}</div>
  </div>
);

const Circle: FC<JSX.IntrinsicElements["div"] & { size: string }> = ({ size, className, style, ...rest }) => {
  const stl: CSSProperties = {
    ...style,
    width: size,
    height: size,
  };
  return <div style={stl} className={classNames("scha__circle", className)} {...rest} />;
};
